import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Box, Typography, Autocomplete, TextField, Chip, Checkbox, useTheme, useMediaQuery, ToggleButtonGroup, ToggleButton, alpha } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { ComposedChart, Area, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse } from 'date-fns';
import { niceScale, formatAxisCurrency } from '../../utils/chartAxisUtils';

const ALL_KEY = '__ALL_CATEGORIES__';
const categoryFilter = createFilterOptions();

const IncomeExpenseChart = ({ 
  data, 
  formatCurrency, 
  categories = [],
  incomeCategoryKeys = [],
  expenseCategoryKeys = [],
  incomeByMonth = {},
  expenseByMonth = {},
  startMonth,
  endMonth,
  onStartMonthChange,
  onEndMonthChange,
  chartMode: controlledChartMode,
  onChartModeChange,
  height = 500
}) => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [focusedLine, setFocusedLine] = useState(null);
  const [hoveredBar, setHoveredBar] = useState(null);
  const [categorySearch, setCategorySearch] = useState('');
  // Chart mode: controlled (via props) or uncontrolled (internal state) pattern
  const [internalChartMode, setInternalChartMode] = useState('area');
  const isControlled = controlledChartMode !== undefined && typeof onChartModeChange === 'function';
  const chartMode = isControlled ? controlledChartMode : internalChartMode;
  const setChartMode = isControlled ? onChartModeChange : setInternalChartMode;
  // Month range state
  const months = useMemo(() => (data || []).map(d => d.month), [data]);
  const parseMonth = m => parse(m, 'MMM-yy', new Date());
  const formatMonth = d => format(d, 'MMM-yy');

  // Preprocess data to ensure all selected categories have values
  const processedData = useMemo(() => {
    if (!data) return [];
    
    return data.map(monthData => {
      const processed = { ...monthData };
      selectedCategories.forEach(category => {
        if (typeof processed[category] !== 'number') {
          processed[category] = 0;
        }
      });
      return processed;
    });
  }, [data, selectedCategories]);

  // Filter data by selected month range
  const filteredData = useMemo(() => {
    if (!startMonth || !endMonth) return processedData;
    return (processedData || []).filter(d => {
      const dMonth = parseMonth(d.month);
      return dMonth >= startMonth && dMonth <= endMonth;
    });
  }, [processedData, startMonth, endMonth]);

  // Classification sets (consistent with Sankey/BudgetProgress via parent-provided keys)
  const incomeKeySet = useMemo(() => new Set(incomeCategoryKeys || []), [incomeCategoryKeys]);
  const expenseKeySet = useMemo(() => new Set(expenseCategoryKeys || []), [expenseCategoryKeys]);

  // For stacked mode, transform data so income categories are positive and expenses are negative
  const stackedTransformedData = useMemo(() => {
    if (!filteredData || !filteredData.length) return filteredData;
    return filteredData.map(row => {
      const out = { ...row };
      Object.keys(row).forEach(k => {
        if (k === 'month') return;
        const v = row[k];
        if (typeof v !== 'number') return;
        // Per-month classification: if this category has positive value in incomeByMonth for this row's month, treat as income;
        // if present in expenseByMonth for this month, treat as expense (values stored as positive in useBudgetData);
        // fallback to global sets when monthly map isn't conclusive.
        const m = row.month;
        const incomeVal = incomeByMonth?.[k]?.[m];
        const expenseVal = expenseByMonth?.[k]?.[m];
        if (typeof incomeVal === 'number' && incomeVal > 0 && (!expenseVal || expenseVal === 0)) {
          out[k] = Math.abs(v);
        } else if (typeof expenseVal === 'number' && expenseVal > 0 && (!incomeVal || incomeVal === 0)) {
          out[k] = -Math.abs(v);
        } else if (incomeKeySet.has(k) && !expenseKeySet.has(k)) {
          out[k] = Math.abs(v);
        } else if (!incomeKeySet.has(k) && expenseKeySet.has(k)) {
          out[k] = -Math.abs(v);
        } else if (incomeKeySet.has(k) && expenseKeySet.has(k)) {
          // Mixed globally but ambiguous this month: decide by value sign if data included raw signs; else default to positive
          out[k] = Math.abs(v);
        } else {
          out[k] = v;
        }
      });
      return out;
    });
  }, [filteredData, incomeKeySet, expenseKeySet]);

  // Single source of truth for everything rendered: income stays positive,
  // expenses are negative in BOTH the area and stacked-bar modes so the two
  // views (and their tooltips) always agree on sign.
  const chartData = stackedTransformedData;

  // Get top 5 categories by default
  const topCategories = useMemo(() => {
    if (!data || !data.length) return [];
    
    // Get all unique categories from the data
    const categoryTotals = {};
    data.forEach(point => {
      Object.keys(point).forEach(key => {
        if (key !== 'month' && key !== 'income' && key !== 'expenses' && key !== 'savings') {
          if (!categoryTotals[key]) {
            categoryTotals[key] = 0;
          }
          categoryTotals[key] += Math.abs(point[key] || 0);
        }
      });
    });
    
    // Sort categories by their total absolute value
    return Object.entries(categoryTotals)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([category]) => category);
  }, [data]);

  // Initialize selected categories with top 5
  React.useEffect(() => {
    if (topCategories.length > 0 && selectedCategories.length === 0) {
      setSelectedCategories(topCategories);
    }
  }, [topCategories]);

  // Categories currently visible in the dropdown given the search text —
  // "Select all" operates on this subset so it composes with searching.
  const filteredCategories = (inputValue) =>
    categoryFilter(groupedCategories, { inputValue, getOptionLabel: displayName });

  const handleCategoryChange = (event, newValue, reason, details) => {
    if (details?.option === ALL_KEY) {
      const filtered = filteredCategories(categorySearch);
      const allFilteredSelected = filtered.length > 0 && filtered.every(c => selectedCategories.includes(c));
      if (allFilteredSelected) {
        setSelectedCategories(selectedCategories.filter(c => !filtered.includes(c)));
      } else {
        setSelectedCategories(Array.from(new Set([...selectedCategories, ...filtered])));
      }
      return;
    }
    setSelectedCategories(newValue.filter(v => v !== ALL_KEY));
  };

  const handleLegendClick = (entry) => {
    if (focusedLine === entry.dataKey) {
      setFocusedLine(null); // Clicking the active legend item removes the focus
    } else {
      setFocusedLine(entry.dataKey);
    }
  };

  const tooltipFormatter = (value) => formatCurrency(value);
  
  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      let filteredPayload = payload;
      if (chartMode === 'stacked' && hoveredBar) {
        filteredPayload = payload.filter(p => p.dataKey === hoveredBar);
      } else if (focusedLine) {
        filteredPayload = payload.filter(p => p.dataKey === focusedLine);
      }

      const rows = [...filteredPayload]
        .filter(p => typeof p.value === 'number')
        .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
      const showNet = rows.length > 1;
      const net = rows.reduce((sum, r) => sum + (r.value || 0), 0);

      return (
        <Box sx={{ bgcolor: 'white', p: 1.5, border: 'none', borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.14)', minWidth: 168 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 700, color: 'text.secondary' }}>{label}</Typography>
          {rows.map((entry, index) => (
            <Box key={`item-${index}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                <Box
                  component="span"
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: entry.color,
                    flexShrink: 0,
                    mr: 1
                  }}
                />
                <Typography variant="caption" noWrap sx={{ color: 'text.primary' }}>
                  {entry.name}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: entry.value < 0 ? 'error.main' : 'text.primary' }}>
                {formatCurrency(entry.value)}
              </Typography>
            </Box>
          ))}
          {showNet && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: 0.75, pt: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Net</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: 'nowrap', color: net < 0 ? 'error.main' : 'success.main' }}>
                {formatCurrency(net)}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }
    return null;
  };

  // Get all available categories for selection
  const availableCategories = useMemo(() => {
    if (!data || !data.length) return [];
    const categories = new Set();
    data.forEach(point => {
      Object.keys(point).forEach(key => {
        if (key !== 'month' && key !== 'income' && key !== 'expenses' && key !== 'savings') {
          categories.add(key);
        }
      });
    });
    return Array.from(categories).sort();
  }, [data]);

  const getLineOpacity = (dataKey) => {
    if (!focusedLine) return 1;
    return focusedLine === dataKey ? 1 : 0.2;
  };

  // Chart data keys are category IDs; resolve to display names for labels
  const categoryDisplayNameMap = useMemo(() => {
    const map = new Map();
    (categories || []).forEach(c => { if (c?.id) map.set(c.id, c.name || c.id); });
    return map;
  }, [categories]);
  const displayName = (key) =>
    key === 'uncategorized' ? 'Uncategorized' : (categoryDisplayNameMap.get(key) || key);

  // Dropdown grouping: income vs expense sections (keys provided by parent,
  // consistent with the Sankey/BudgetProgress classification).
  const groupForCategory = (key) => {
    const isIncome = incomeKeySet.has(key);
    const isExpense = expenseKeySet.has(key);
    if (isIncome && !isExpense) return 'Income';
    if (isExpense && !isIncome) return 'Expenses';
    if (isIncome && isExpense) return 'Mixed';
    return 'Other';
  };
  const groupOrder = ['Income', 'Expenses', 'Mixed', 'Other'];
  // Options sorted by group then name — Autocomplete's groupBy requires
  // options of the same group to be contiguous.
  const groupedCategories = useMemo(() => {
    return [...availableCategories].sort((a, b) => {
      const ga = groupOrder.indexOf(groupForCategory(a));
      const gb = groupOrder.indexOf(groupForCategory(b));
      if (ga !== gb) return ga - gb;
      return displayName(a).localeCompare(displayName(b));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCategories, incomeKeySet, expenseKeySet, categoryDisplayNameMap]);

  // Color helpers: prefer real category colors from categories prop, fallback to computed palette
  const categoryColorMap = useMemo(() => {
    const map = new Map();
    (categories || []).forEach(c => {
      if (c?.name) map.set(c.name, c.color || null);
      if (c?.id) map.set(c.id, c.color || null);
    });
    return map;
  }, [categories]);
  const colorForIndex = (index, alpha = 1) => `hsla(${(index * 137.5) % 360}, 70%, 50%, ${alpha})`;
  const colorForCategory = (name, index, alpha = 1) => {
    const c = categoryColorMap.get(name);
    if (c) {
      // If stored color is in theme token form, leave as-is; if hex/rgb, apply alpha when needed
      if (alpha === 1) return c;
      // naive alpha for hex colors (#rrggbb)
      if (/^#([0-9a-f]{6}|[0-9a-f]{3})$/i.test(c)) return c;
      return c; // let browser handle other formats
    }
    return colorForIndex(index, alpha);
  };
  const gradientIdFor = (key) => `areaGradient-${String(key).replace(/[^a-zA-Z0-9]/g, '-')}`;
  
  // Y axis domain + round ("nice") ticks. Sourced from chartData so both modes
  // share the expenses-are-negative convention.
  const yAxis = useMemo(() => {
    const domainData = chartData;
    const fallback = { domain: ['auto', 'auto'], ticks: undefined };
    if (!domainData || domainData.length === 0) return fallback;
    // Focused line: zoom to that series (clamped to include zero for an honest baseline)
    if (focusedLine) {
      const values = domainData.map(point => point[focusedLine]).filter(v => typeof v === 'number');
      if (!values.length) return fallback;
      let min = Math.min(...values);
      let max = Math.max(...values);
      if (min >= 0) min = 0;
      if (max <= 0) max = 0;
      return niceScale(min, max);
    }
    if (selectedCategories.length === 0) return fallback;
    if (chartMode === 'stacked') {
      // Stacked positive max & stacked negative min so negative-only categories still render
      let maxPos = 0;
      let minNeg = 0;
      domainData.forEach(point => {
        let posSum = 0;
        let negSum = 0;
        selectedCategories.forEach(cat => {
          const v = point[cat];
          if (typeof v === 'number') {
            if (v >= 0) posSum += v; else negSum += v; // negSum accumulates (more negative)
          }
        });
        if (posSum > maxPos) maxPos = posSum;
        if (negSum < minNeg) minNeg = negSum;
      });
      if (maxPos === 0 && minNeg === 0) return fallback;
      return niceScale(minNeg, maxPos);
    }
    // Area (multi-line) mode: each series is independent, so span all values
    const allValues = [];
    domainData.forEach(point => {
      selectedCategories.forEach(cat => { if (typeof point[cat] === 'number') allValues.push(point[cat]); });
    });
    if (!allValues.length) return fallback;
    let min = Math.min(...allValues);
    let max = Math.max(...allValues);
    if (min >= 0) min = 0;
    if (max <= 0) max = 0;
    return niceScale(min, max);
  }, [focusedLine, chartData, selectedCategories, chartMode]);

  // Calculate trend line data for focused category
  const trendLineData = useMemo(() => {
    if (!focusedLine || !chartData.length) return null;

    const values = chartData.map(point => point[focusedLine]).filter(v => typeof v === 'number');
    if (values.length === 0) return null;

    const average = values.reduce((sum, val) => sum + val, 0) / values.length;

    return chartData.map(point => ({
      month: point.month,
      trend: average
    }));
  }, [focusedLine, chartData]);

  // Custom legend: one pill chip per selected category (matches the account
  // chips under ProjectedBalancesCard). Click focuses/unfocuses the series.
  const CustomLegend = ({ selectedCategories, focusedLine, onLegendClick }) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.75, mt: 2 }}>
      {selectedCategories.map((category, index) => {
        const color = colorForCategory(category, index, 1);
        const dimmed = Boolean(focusedLine) && focusedLine !== category;
        return (
          <Chip
            key={category}
            size="small"
            variant={dimmed ? 'outlined' : 'filled'}
            onClick={() => onLegendClick({ dataKey: category })}
            icon={
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  ml: '6px !important',
                  borderRadius: '50%',
                  bgcolor: dimmed ? 'grey.400' : color,
                }}
              />
            }
            label={displayName(category)}
            sx={{
              height: 24,
              fontSize: '0.75rem',
              fontWeight: focusedLine === category ? 600 : 400,
              bgcolor: dimmed ? 'transparent' : alpha(color, 0.12),
              color: dimmed ? 'text.secondary' : 'text.primary',
              '&:hover': { bgcolor: alpha(color, 0.2) },
            }}
          />
        );
      })}
    </Box>
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const segmentBorder = theme.palette?.background?.paper || '#fff';

  // Custom label for focused line on mobile: only show first, last, min, max
  const getKeyLabelIndices = (values) => {
    if (!values.length) return [];
    let minIdx = 0, maxIdx = 0;
    values.forEach((v, i) => {
      if (v < values[minIdx]) minIdx = i;
      if (v > values[maxIdx]) maxIdx = i;
    });
    const indices = new Set([0, values.length - 1, minIdx, maxIdx]);
    return Array.from(indices);
  };
  const mobileFocusedLabel = (props) => {
    const { x, y, value, index } = props;
    const values = chartData.map(point => point[focusedLine]);
    const keyIndices = getKeyLabelIndices(values);
    if (!keyIndices.includes(index)) return null;
    return (
      <text x={x} y={y - 8} textAnchor="middle" fontSize={11} fill={`hsl(${(selectedCategories.indexOf(focusedLine) * 137.5) % 360}, 70%, 50%)`}>
        {formatCurrency(value)}
      </text>
    );
  };

  // Custom label for trend (average) line on mobile: only show at last point
  const mobileTrendLabel = (props) => {
    const { x, y, value, index } = props;
    if (index !== trendLineData.length - 1) return null;
    return (
      <text x={x} y={y - 8} textAnchor="middle" fontSize={11} fill="rgba(0,0,0,0.5)">
        {formatCurrency(value)}
      </text>
    );
  };

  // Rounded corners removed per request

  return (
    <Box>
      {/* Controls Row: Date pickers and category selector */}
      <Box
        sx={{
          display: { xs: 'grid', sm: 'flex' },
          gridTemplateColumns: { xs: 'minmax(0, 1fr) minmax(0, 1fr) minmax(100px, 1.15fr)' },
          flexDirection: 'row',
          flexWrap: { sm: 'wrap' },
          gap: { xs: 1, sm: 2 },
          mb: 2,
          alignItems: 'center',
          fontSize: 14,
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: { xs: 'contents', sm: 'flex' }, gap: 2 }}>
            <DatePicker
              label="Start Month"
              views={['year', 'month']}
              minDate={months.length ? parseMonth(months[0]) : undefined}
              maxDate={endMonth}
              value={startMonth}
              onChange={onStartMonthChange}
              format="MMM-yy"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    minWidth: 0,
                    width: { xs: '100%', sm: 'auto' },
                    '& .MuiInputLabel-root': { fontSize: { xs: '0.75rem', sm: '1rem' } },
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                      px: { xs: 0.75, sm: 1.75 },
                    },
                    '& .MuiInputAdornment-root': { ml: { xs: 0, sm: 1 } },
                    '& .MuiIconButton-root': { p: { xs: 0.5, sm: 1 } },
                    '& .MuiSvgIcon-root': { fontSize: { xs: '1.1rem', sm: '1.5rem' } },
                  },
                },
              }}
            />
            <DatePicker
              label="End Month"
              views={['year', 'month']}
              minDate={startMonth}
              maxDate={months.length ? parseMonth(months[months.length - 1]) : undefined}
              value={endMonth}
              onChange={onEndMonthChange}
              format="MMM-yy"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    minWidth: 0,
                    width: { xs: '100%', sm: 'auto' },
                    '& .MuiInputLabel-root': { fontSize: { xs: '0.75rem', sm: '1rem' } },
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                      px: { xs: 0.75, sm: 1.75 },
                    },
                    '& .MuiInputAdornment-root': { ml: { xs: 0, sm: 1 } },
                    '& .MuiIconButton-root': { p: { xs: 0.5, sm: 1 } },
                    '& .MuiSvgIcon-root': { fontSize: { xs: '1.1rem', sm: '1.5rem' } },
                  },
                },
              }}
            />
          </Box>
        </LocalizationProvider>
        <Autocomplete
          multiple
          size="small"
          disableCloseOnSelect
          options={groupedCategories}
          value={selectedCategories}
          onChange={handleCategoryChange}
          inputValue={categorySearch}
          onInputChange={(event, value, reason) => {
            // 'reset' fires after each option toggle; ignoring it keeps the
            // search text so users can toggle several matches in one pass.
            if (reason !== 'reset') setCategorySearch(value);
          }}
          onClose={() => setCategorySearch('')}
          getOptionLabel={(option) => (option === ALL_KEY ? 'Select all' : displayName(option))}
          groupBy={(option) => (option === ALL_KEY ? '' : groupForCategory(option))}
          filterOptions={(options, state) => {
            const filtered = categoryFilter(options, state);
            // Pin a select-all row above the matches
            return filtered.length ? [ALL_KEY, ...filtered] : [];
          }}
          renderGroup={(params) => (
            <li key={params.key}>
              {params.group && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    px: 2,
                    pt: 1,
                    pb: 0.25,
                    fontWeight: 700,
                    color: 'text.secondary',
                    position: 'sticky',
                    top: -8,
                    zIndex: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  {params.group}
                </Typography>
              )}
              <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
            </li>
          )}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            if (option === ALL_KEY) {
              const filtered = filteredCategories(categorySearch);
              const selectedCount = filtered.filter(c => selectedCategories.includes(c)).length;
              return (
                <li key={ALL_KEY} {...optionProps}>
                  <Checkbox
                    size="small"
                    checked={filtered.length > 0 && selectedCount === filtered.length}
                    indeterminate={selectedCount > 0 && selectedCount < filtered.length}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {categorySearch ? 'Select all matches' : 'Select all'}
                  </Typography>
                </li>
              );
            }
            const catIdx = availableCategories.indexOf(option);
            return (
              <li key={option} {...optionProps}>
                <Checkbox size="small" checked={selected} />
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: colorForCategory(option, catIdx, 1),
                    mr: 1,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" noWrap>{displayName(option)}</Typography>
              </li>
            );
          }}
          // Count-only summary keeps the trigger a fixed size; the legend
          // below the chart is the single place selections are enumerated.
          renderTags={(value) => (
            <Typography
              variant="body2"
              sx={{
                pl: 0.5,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}
            >
              {value.length === availableCategories.length && availableCategories.length > 0
                ? 'All categories'
                : `${value.length} selected`}
            </Typography>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Categories"
              placeholder={selectedCategories.length === 0 ? 'Select…' : undefined}
              InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
            />
          )}
          sx={{
            width: { xs: '100%', sm: 170 },
            minWidth: 0,
            // Keep the mobile trigger compact enough to share the row with
            // both month fields while preserving the full desktop sizing.
            '& .MuiInputLabel-root': { fontSize: { xs: '0.75rem', sm: '1rem' } },
            '& .MuiOutlinedInput-root': {
              flexWrap: 'nowrap',
              px: { xs: 0.5, sm: 1 },
              pr: { xs: '28px !important', sm: '39px !important' },
            },
            '& .MuiAutocomplete-input': { minWidth: 0 },
            '& .MuiAutocomplete-endAdornment': { right: { xs: 2, sm: 9 } },
            '& .MuiAutocomplete-popupIndicator': { p: { xs: 0.25, sm: 1 } },
            '& .MuiSvgIcon-root': { fontSize: { xs: '1.1rem', sm: '1.5rem' } },
          }}
          // Keep the dropdown wide enough for long category names even
          // though the trigger itself only shows a short count summary.
          slotProps={{ popper: { style: { minWidth: 260, width: 'auto' } } }}
          ListboxProps={{ style: { maxHeight: 320 } }}
        />
  {/* Toggle removed here; appears only if component is uncontrolled */}
  {!isControlled && (
          <Box sx={{ ml: 'auto' }}>
            <ToggleButtonGroup
              size="small"
              value={chartMode}
              exclusive
              onChange={(_, val) => { if (val) { setChartMode(val); setFocusedLine(null); } }}
              aria-label="Chart mode"
            >
              <ToggleButton value="area" aria-label="Area / line mode" sx={{ px: 1 }}>
                <ShowChartIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="stacked" aria-label="Stacked bar mode" sx={{ px: 1 }}>
                <StackedBarChartIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
      </Box>
  <Box sx={{ height, bgcolor: 'grey.50', p: 0, borderRadius: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            stackOffset="sign"
            barCategoryGap="20%"
          >
            <defs>
        {selectedCategories.map((key, index) => (
                <linearGradient
                  key={key}
                  id={gradientIdFor(key)}
                  x1="0" y1="0" x2="0" y2="1"
                >
          <stop offset="0%" stopColor={colorForCategory(key, index, 1)} stopOpacity={0.28} />
          <stop offset="60%" stopColor={colorForCategory(key, index, 1)} stopOpacity={0.12} />
          <stop offset="100%" stopColor={colorForCategory(key, index, 1)} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eceff1" />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#78909c' }} tickMargin={8} axisLine={{ stroke: '#e0e0e0' }} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: '#78909c' }} tickMargin={6} tickFormatter={formatAxisCurrency} domain={yAxis.domain} ticks={yAxis.ticks} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#b0bec5', strokeWidth: 1 }} wrapperStyle={{ fontSize: 11 }} />
            {/* Zero baseline: shown whenever the chart spans negative values (expenses) */}
            {(chartMode === 'stacked' || (Array.isArray(yAxis.domain) && yAxis.domain[0] < 0)) && (
              <ReferenceLine y={0} stroke="#b0bec5" strokeWidth={1} />
            )}
            {/* Render trend line when a category is focused */}
            {chartMode === 'area' && focusedLine && trendLineData && (
              <Line
                type="linear"
                dataKey="trend"
                data={trendLineData}
                stroke="rgba(0, 0, 0, 0.5)"
                strokeDasharray="5 5"
                dot={false}
                name={`${displayName(focusedLine)} Trend`}
                strokeWidth={1}
                label={isMobile ? mobileTrendLabel : {
                  position: 'top',
                  formatter: (value) => formatCurrency(value),
                  fill: 'rgba(0, 0, 0, 0.5)',
                  fontSize: 11,
                  offset: 10
                }}
              />
            )}
            {/* Chart series rendering depending on mode */}
            {chartMode === 'area' && (
              focusedLine
                ? (() => {
                    const index = selectedCategories.indexOf(focusedLine);
                    if (index !== -1) {
                      return (
                        <Area
                          key={focusedLine}
                          type="monotone"
                          dataKey={focusedLine}
                          name={displayName(focusedLine)}
                          stroke={colorForCategory(focusedLine, index, 1)}
                          fill={`url(#${gradientIdFor(focusedLine)})`}
                          activeDot={{ r: 6, stroke: colorForCategory(focusedLine, index, 1), strokeWidth: 2, fill: 'white' }}
                          dot={{ r: isMobile ? 2 : 3, stroke: colorForCategory(focusedLine, index, 1), strokeWidth: 1, fill: 'white' }}
                          strokeWidth={2.5}
                          opacity={1}
                          label={isMobile ? mobileFocusedLabel : {
                            position: 'top',
                            formatter: (value) => formatCurrency(value),
                            fill: colorForCategory(focusedLine, index, 1),
                            fontSize: 11,
                            offset: 10
                          }}
                        />
                      );
                    }
                    return null;
                  })()
                : selectedCategories.map((category, index) => (
                    // No-focus view: clean lines (no overlapping fills) so 5 series
                    // stay legible instead of blending into a single colour wash.
                    <Area
                      key={category}
                      type="monotone"
                      dataKey={category}
                      name={displayName(category)}
                      stroke={colorForCategory(category, index, 1)}
                      fill="none"
                      activeDot={{ r: 4, stroke: colorForCategory(category, index, 1), strokeWidth: 2, fill: 'white' }}
                      dot={{ r: isMobile ? 2 : 3, stroke: colorForCategory(category, index, 1), strokeWidth: 1, fill: 'white' }}
                      strokeWidth={2}
                      opacity={getLineOpacity(category)}
                      isAnimationActive={true}
                    />
                  ))
            )}
            {chartMode === 'stacked' && (
              selectedCategories.map((category, index) => (
                <Bar
                  key={category}
                  dataKey={category}
                  name={displayName(category)}
                  stackId="stack"
                  fill={colorForCategory(category, index, 0.9)}
                  stroke={segmentBorder}
                  strokeWidth={1.2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  opacity={getLineOpacity(category)}
                  isAnimationActive={true}
                  onMouseEnter={() => setHoveredBar(category)}
                  onMouseLeave={() => setHoveredBar(null)}
                />
              ))
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
      {/* Custom legend always shows all selected categories, below the chart */}
      <CustomLegend
        selectedCategories={selectedCategories}
        focusedLine={focusedLine}
        onLegendClick={handleLegendClick}
      />
    </Box>
  );
};

export default IncomeExpenseChart;